import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, AlertTriangle, CreditCard, Banknote, Plus, Check, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PHONE = "07876664591";

const EMPTY = { name: "", phone: "", email: "", address_line1: "", address_line2: "", city: "", postcode: "", notes: "" };

export default function CheckoutDialog({ open, onOpenChange, deliveryFee = 2, minOrder = 10, subtotalPaid = 0 }) {
  const { items, clear } = useCart();
  const { user, refresh: refreshAuth } = useAuth();
  const total = subtotalPaid + (subtotalPaid > 0 ? deliveryFee : 0);
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [pcStatus, setPcStatus] = useState(null);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storeOpen, setStoreOpen] = useState(true);
  const [storeLabel, setStoreLabel] = useState("");
  const [selectedAddrId, setSelectedAddrId] = useState(null); // null = new address; "id" = saved

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addresses = user?.addresses || [];

  // Pre-fill from user + default address on open
  useEffect(() => {
    if (!open || !user) return;
    setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
    if (addresses.length > 0 && !selectedAddrId) {
      const def = addresses.find((a) => a.is_default) || addresses[0];
      applyAddress(def);
    }
    // eslint-disable-next-line
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    api.get("/store/status").then(({ data }) => {
      setStoreOpen(!!data.is_open);
      setStoreLabel(!data.is_open && data.next_open_label ? `Closed — re-opens ${data.next_open_label}` : "");
    }).catch(() => {});
  }, [open]);

  const applyAddress = (a) => {
    setSelectedAddrId(a.id);
    setForm((f) => ({
      ...f,
      address_line1: a.address_line1 || "",
      address_line2: a.address_line2 || "",
      city: a.city || "",
      postcode: a.postcode || "",
      notes: a.notes || "",
    }));
    setPcStatus({ valid: true, postcode: a.postcode, area: "Saved address" });
  };

  const useDifferent = () => {
    setSelectedAddrId(null);
    setForm((f) => ({ ...f, address_line1: "", address_line2: "", city: "", postcode: "", notes: "" }));
    setPcStatus(null);
  };

  const validatePostcode = async () => {
    if (!form.postcode.trim()) return;
    setValidating(true); setPcStatus(null);
    try {
      const { data } = await api.post("/postcode/validate", { postcode: form.postcode });
      setPcStatus(data);
    } catch {
      setPcStatus({ valid: false, reason: "Could not validate postcode. Try again." });
    } finally { setValidating(false); }
  };

  const belowMin = subtotalPaid < minOrder;

  const submit = async () => {
    if (!storeOpen) { toast.error("Sorry, we're closed right now."); return; }
    if (belowMin) { toast.error(`Minimum order £${minOrder.toFixed(2)}.`); return; }
    if (!pcStatus?.valid) { toast.error("Please enter a valid postcode in our delivery zone."); return; }
    if (!form.name || !form.phone || !form.email || !form.address_line1 || !form.city) {
      toast.error("Please fill in all required delivery details."); return;
    }
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => {
          if (it.reward_id) {
            return {
              reward_id: it.reward_id, reward_type: it.reward_type,
              combo_pasta: it.combo_pasta, combo_wings: it.combo_wings,
              is_combo: it.reward_type === "free_combo", quantity: 1,
            };
          }
          return it.is_combo
            ? { is_combo: true, combo_pasta: it.combo_pasta, combo_wings: it.combo_wings, quantity: it.quantity }
            : { is_combo: false, item_id: it.item_id, quantity: it.quantity };
        }),
        delivery: form,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        save_address: !!user && !selectedAddrId,
      };
      const { data } = await api.post("/orders", payload);
      if (paymentMethod === "card") {
        window.location.href = data.checkout_url;
      } else {
        clear();
        await refreshAuth();
        toast.success("Cash order placed — pay on delivery");
        nav(`/order/${data.order_id}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create order.";
      toast.error(typeof msg === "string" ? msg : "Could not create order.");
      setSubmitting(false);
    }
  };

  const blocked = !storeOpen || belowMin || !pcStatus?.valid || submitting;
  const showInlineAddressForm = !user || addresses.length === 0 || selectedAddrId === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-2 border-gold rounded-none max-w-2xl p-0 max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-cream uppercase tracking-tight text-3xl">Delivery Details</DialogTitle>
            <DialogDescription className="text-cream/70 font-body">
              We deliver to <span className="text-gold">Partington · Carrington · Warburton</span>. £{minOrder.toFixed(2)} min order, £{deliveryFee.toFixed(2)} delivery.
            </DialogDescription>
          </DialogHeader>

          {!storeOpen && storeLabel && (
            <div className="mt-4 border-2 border-accent/60 p-3 text-accent flex items-center gap-2 text-sm font-heading uppercase tracking-widest" data-testid="checkout-closed-banner">
              <AlertTriangle className="h-4 w-4" /> {storeLabel}
            </div>
          )}

          {/* Saved Addresses Picker */}
          {user && addresses.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="saved-addresses-picker">
              <div className="font-heading uppercase tracking-widest text-xs text-cream/70">Deliver to</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {addresses.map((a) => {
                  const active = selectedAddrId === a.id;
                  return (
                    <button
                      key={a.id}
                      onClick={() => applyAddress(a)}
                      className={`text-left p-3 border-2 transition-colors ${active ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
                      data-testid={`saved-address-${a.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-heading uppercase tracking-tight text-cream text-base leading-none">
                          {a.label || "Address"}{a.is_default && <span className="ml-2 text-gold text-[10px] tracking-widest">DEFAULT</span>}
                        </div>
                        {active && <Check className="h-4 w-4 text-gold shrink-0" />}
                      </div>
                      <div className="text-cream/70 text-xs mt-1">
                        {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}, {a.city} {a.postcode}
                      </div>
                    </button>
                  );
                })}
                <button
                  onClick={useDifferent}
                  className={`text-left p-3 border-2 border-dashed transition-colors flex items-center gap-2 font-heading uppercase tracking-widest text-sm ${selectedAddrId === null ? "border-gold text-gold" : "border-border text-cream/60 hover:border-gold/60"}`}
                  data-testid="use-different-address-btn"
                >
                  <Plus className="h-4 w-4" /> Different address
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 space-y-4">
            {showInlineAddressForm && (
              <>
                <div className="border border-border p-4">
                  <label className="font-heading uppercase tracking-widest text-xs text-cream/70">Postcode *</label>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text" value={form.postcode}
                      onChange={(e) => { set("postcode", e.target.value); setPcStatus(null); }}
                      className="flex-1 bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream uppercase tracking-wider"
                      placeholder="e.g. M31 4DW or WA13 9SS"
                      data-testid="postcode-input"
                    />
                    <button onClick={validatePostcode} disabled={validating || !form.postcode.trim()} className="btn-poster" data-testid="postcode-check-btn">
                      {validating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Check"}
                    </button>
                  </div>
                  {pcStatus && (
                    <div className={`mt-2 text-sm ${pcStatus.valid ? "text-gold" : "text-accent"}`} data-testid="postcode-result">
                      {pcStatus.valid ? (
                        <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {pcStatus.area} — in delivery zone</span>
                      ) : pcStatus.reason}
                    </div>
                  )}
                </div>
                <Field label="Address Line 1 *" value={form.address_line1} onChange={(v) => set("address_line1", v)} testid="address1-input" />
                <Field label="Address Line 2" value={form.address_line2} onChange={(v) => set("address_line2", v)} testid="address2-input" />
                <Field label="City *" value={form.city} onChange={(v) => set("city", v)} testid="city-input" />
                <div>
                  <label className="font-heading uppercase tracking-widest text-xs text-cream/70">Delivery Notes</label>
                  <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    className="mt-1 w-full bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream"
                    data-testid="notes-input" />
                </div>

                {user && addresses.length < 8 && (
                  <div className="border border-gold/30 bg-gold/5 p-3 text-xs text-cream/70" data-testid="save-address-section">
                    <div className="font-heading uppercase tracking-widest text-gold/90 text-[10px]">
                      We'll save this address to your account ({addresses.length}/8)
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="border-t border-border pt-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Full Name *" value={form.name} onChange={(v) => set("name", v)} testid="name-input" />
                <Field label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} testid="phone-input" />
              </div>
              <div className="mt-3">
                <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="email-input" />
              </div>
            </div>

            <div className="border-2 border-accent/70 bg-accent/10 p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <div>
                <div className="font-heading uppercase tracking-widest text-accent text-sm">Food Allergy or Intolerance?</div>
                <p className="text-cream/80 text-sm mt-1">
                  Do <strong>not</strong> place your order online. Please call us directly before ordering so we can advise you safely.
                </p>
                <a href={`tel:${PHONE}`} className="mt-2 inline-flex items-center gap-2 font-heading text-accent text-lg tracking-wider hover:text-amber-400 transition-colors">
                  <Phone className="h-4 w-4" /> {PHONE}
                </a>
              </div>
            </div>

            <div>
              <label className="font-heading uppercase tracking-widest text-xs text-cream/70">Payment Method *</label>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <PayOption active={paymentMethod === "card"} onClick={() => setPaymentMethod("card")} icon={<CreditCard className="h-5 w-5" />} title="Pay by Card" subtitle="Secure online payment via Stripe" testid="pay-card" />
                <PayOption active={paymentMethod === "cash"} onClick={() => setPaymentMethod("cash")} icon={<Banknote className="h-5 w-5" />} title="Cash on Delivery" subtitle="Pay our driver on the doorstep" testid="pay-cash" />
              </div>
            </div>
          </div>

          <div className="mt-6 border-t border-border pt-5 space-y-1.5">
            <SumRow label="Subtotal" value={`£${subtotalPaid.toFixed(2)}`} />
            <SumRow label="Delivery fee" value={`£${deliveryFee.toFixed(2)}`} />
            <div className="flex items-baseline justify-between mt-1">
              <span className="font-heading uppercase tracking-widest text-cream text-sm">Total</span>
              <span className="font-heading text-gold text-3xl" data-testid="checkout-total">£{total.toFixed(2)}</span>
            </div>
            {belowMin && (
              <div className="text-accent text-xs font-heading uppercase tracking-widest mt-2" data-testid="checkout-min-warn">
                <AlertTriangle className="inline h-3 w-3 mr-1" /> Minimum order £{minOrder.toFixed(2)}. Add £{(minOrder - subtotalPaid).toFixed(2)} more.
              </div>
            )}
            <button onClick={submit} disabled={blocked} className={`w-full btn-poster mt-4 ${blocked ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="pay-now-btn">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
              {paymentMethod === "card" ? "Pay with Card" : "Place Cash Order"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-cream/70">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream"
        data-testid={testid} />
    </div>
  );
}

function SumRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-heading uppercase tracking-widest text-cream/60 text-xs">{label}</span>
      <span className="font-heading text-cream text-lg">{value}</span>
    </div>
  );
}

function PayOption({ active, onClick, icon, title, subtitle, testid }) {
  return (
    <button onClick={onClick}
      className={`text-left p-3 border-2 transition-colors ${active ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
      data-testid={testid}>
      <div className="font-heading uppercase tracking-tight text-cream text-lg leading-none flex items-center gap-2">
        {icon} {title}
      </div>
      <div className="text-cream/60 text-xs mt-1">{subtitle}</div>
    </button>
  );
}
